import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { red, yellow, green } from '@mui/material/colors';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { useTranslation } from 'react-i18next';

const getColorForRisk = (riskColor: string) => {
    switch (riskColor?.toLowerCase()) {
        case 'red':
            return red[500];
        case 'yellow':
            return yellow[700];
        case 'green':
            return green[500];
        default:
            return 'grey.500';
    }
};

const ReportRenderer = ({ report }: { report: any }) => {
    const { t } = useTranslation();

    const translateAIValue = (value: string) => {
        const key = `aiValues.${value.split('(')[0].trim()}`;
        const translation = t(key);
        // If the key is not found, t returns the key itself.
        // In that case, we should return the original value from the AI.
        return translation === key ? value : translation;
    };

    if (!report) {
        return <Typography>No report data available.</Typography>;
    }

    if (report.isFallback) {
        return (
            <Paper elevation={1} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>{report.title}</Typography>
                <Box mt={3}>
                    <Typography variant="h6">{report.processingSummary.title}</Typography>
                    <Typography paragraph>{report.processingSummary.status}</Typography>
                    <Typography paragraph>{report.processingSummary.framesProcessed}</Typography>
                </Box>
                <Box mt={3}>
                    <Typography variant="h6">{report.reasons.title}</Typography>
                    <ul>
                        {report.reasons.items.map((item: string, index: number) => <li key={index}><Typography>{item}</Typography></li>)}
                    </ul>
                </Box>
                <Box mt={3}>
                    <Typography variant="h6">{report.recommendations.title}</Typography>
                    <ol>
                        {report.recommendations.items.map((item: string, index: number) => <li key={index}><Typography>{item}</Typography></li>)}
                    </ol>
                </Box>
                <Typography variant="caption" sx={{ mt: 4, display: 'block' }}>{report.footer}</Typography>
            </Paper>
        );
    }
    
    return (
        <Box>
            {/* Executive Summary */}
            <Box mb={5}>
                <Typography variant="h4" component="h2" gutterBottom>{report.executiveSummary.title}</Typography>
                <MarkdownPreview source={report.executiveSummary.content} style={{ background: 'transparent' }} />
                <Box display="flex" flexWrap="wrap" mt={2} sx={{ mx: -1.5 }}>
                    {report.executiveSummary.stats.map((stat: any, i: number) => (
                        <Box key={i} sx={{ width: { xs: '100%', sm: '50%' }, p: 1.5 }}>
                            <Paper sx={{p: 2, textAlign: 'center', height: '100%'}}>
                                <Typography variant="h6">{stat.value}</Typography>
                                <Typography variant="body1">{stat.label}</Typography>
                            </Paper>
                        </Box>
                    ))}
                </Box>
                <Box mt={3}>
                    <Typography variant="h5" component="h3" gutterBottom>{report.executiveSummary.riskProfile.title}</Typography>
                     <Box display="flex" flexWrap="wrap" sx={{ mx: -1 }}>
                        {report.executiveSummary.riskProfile.stats.map((stat: any, i: number) => (
                           <Box key={i} sx={{ width: { xs: '50%', md: '25%' }, p: 1 }}>
                               <Paper sx={{p: 2, textAlign: 'center', height: '100%', borderTop: `4px solid ${getColorForRisk(stat.color)}`}}>
                                    <Typography variant="h6">{stat.value}</Typography>
                                    <Typography variant="body2">{stat.label}</Typography>
                                </Paper>
                           </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Detailed Observations */}
            <Box mb={5}>
                <Typography variant="h4" component="h2" gutterBottom>{report.detailedObservations.title}</Typography>
                {report.detailedObservations.sections.map((section: any, i: number) => (
                    <Box key={i} mb={4}>
                        <Typography variant="h5" component="h3" sx={{ borderBottom: `2px solid ${getColorForRisk(section.color)}`, pb: 1, mb: 2 }}>{section.title}</Typography>
                        {section.issues.map((issue: any, j: number) => {
                            const actionText = issue.action === 'Maintain current practice'
                                ? t('reportRenderer.maintainPractice')
                                : issue.action;

                            return (
                                <Paper key={j} sx={{ p: 3, mb: 3, borderLeft: `5px solid ${getColorForRisk(issue.riskColor)}` }}>
                                    <Typography variant="h6" gutterBottom>{t('reportRenderer.issue', { number: j + 1 })}: {issue.description}</Typography>
                                    
                                    {/* Desktop layout: image and content side by side */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: { xs: 'column', md: 'row' }, 
                                        gap: 3,
                                        alignItems: { md: 'flex-start' }
                                    }}>
                                        {/* Image container */}
                                        {issue.dataUrl && (
                                            <Box sx={{ 
                                                width: { xs: '100%', md: '400px' },
                                                flexShrink: 0
                                            }}>
                                                <img 
                                                    src={issue.dataUrl} 
                                                    alt={`Safety observation frame showing: ${issue.description}`} 
                                                    style={{ 
                                                        width: '100%', 
                                                        height: 'auto',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }} 
                                                />
                                            </Box>
                                        )}
                                        
                                        {/* Content container */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box display="flex" flexWrap="wrap" sx={{ mx: -1, mb: 2 }}>
                                                <Box sx={{ width: { xs: '100%', lg: '50%' }, p: 1 }}>
                                                    <Typography><strong>{t('reportRenderer.riskCategory')}:</strong> {translateAIValue(issue.riskCategory)}</Typography>
                                                    <Typography><strong>{t('reportRenderer.riskScore')}:</strong> {issue.riskScore}/25</Typography>
                                                    <Typography><strong>{t('reportRenderer.sifPotential')}:</strong> {translateAIValue(issue.sifPotential)}</Typography>
                                                </Box>
                                                <Box sx={{ width: { xs: '100%', lg: '50%' }, p: 1 }}>
                                                    <Typography><strong>{t('reportRenderer.severity')}:</strong> {translateAIValue(issue.severity)}</Typography>
                                                    <Typography><strong>{t('reportRenderer.likelihood')}:</strong> {translateAIValue(issue.likelihood)}</Typography>
                                                </Box>
                                            </Box>
                                            <Typography><strong>{t('reportRenderer.recommendedAction')}:</strong> {actionText}</Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ))}
            </Box>

            {/* Risk Analysis Summary */}
            <Box>
                <Typography variant="h4" component="h2" gutterBottom>{report.riskAnalysisSummary.title}</Typography>
                
                {/* Risk Level Distribution */}
                <TableContainer component={Paper} sx={{mb: 4}}>
                    <Typography variant="h6" sx={{p: 2}}>{report.riskAnalysisSummary.riskLevelDistribution.title}</Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {report.riskAnalysisSummary.riskLevelDistribution.headers.map((header: string) => <TableCell key={header}><strong>{header}</strong></TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report.riskAnalysisSummary.riskLevelDistribution.rows.map((row: any, i: number) => (
                                <TableRow key={i}>
                                    <TableCell>{row.level}</TableCell>
                                    <TableCell>{row.count}</TableCell>
                                    <TableCell>{row.priority}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Severity Distribution */}
                <TableContainer component={Paper} sx={{mb: 4}}>
                    <Typography variant="h6" sx={{p: 2}}>{report.riskAnalysisSummary.severityDistribution.title}</Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {report.riskAnalysisSummary.severityDistribution.headers.map((header: string) => <TableCell key={header}><strong>{header}</strong></TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report.riskAnalysisSummary.severityDistribution.rows.map((row: any, i: number) => (
                                <TableRow key={i}>
                                    <TableCell>{row.level}</TableCell>
                                    <TableCell>{row.count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* Category Analysis */}
                <TableContainer component={Paper} sx={{mb: 4}}>
                    <Typography variant="h6" sx={{p: 2}}>{report.riskAnalysisSummary.categoryAnalysis.title}</Typography>
                     <Table>
                        <TableHead>
                            <TableRow>
                                {report.riskAnalysisSummary.categoryAnalysis.headers.map((header: string) => <TableCell key={header}><strong>{header}</strong></TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report.riskAnalysisSummary.categoryAnalysis.rows.map((row: any, i: number) => (
                                <TableRow key={i}>
                                    <TableCell>{translateAIValue(row.category)}</TableCell>
                                    <TableCell>{row.count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* SIF Potential */}
                {report.riskAnalysisSummary.sifPotential && (
                    <Paper sx={{ p: 3, mt: 4, backgroundColor: red[50] }}>
                        <Typography variant="h6" component="h4" gutterBottom>{report.riskAnalysisSummary.sifPotential.title}</Typography>
                        <MarkdownPreview source={report.riskAnalysisSummary.sifPotential.content} style={{ background: 'transparent' }}/>
                        <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                            {report.riskAnalysisSummary.sifPotential.issues.map((issue: any, i: number) => {
                                const translatedCategory = translateAIValue(issue.category);
                                const markdownContent = `**${translatedCategory}:** ${issue.description}`;
                                return (
                                    <Box component="li" key={i} sx={{ mb: 1, '&::marker': { content: '""' } }}>
                                        <MarkdownPreview source={markdownContent} style={{ background: 'transparent' }}/>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                )}

            </Box>
            
            <Typography variant="caption" sx={{ mt: 5, display: 'block', textAlign: 'center' }}>{report.footer}</Typography>
        </Box>
    );
};

export default ReportRenderer; 